import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Stack,
  Container,
  Card,
  CardContent,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Requester from '../../../utils/requester';
import { useNotification } from '../../../context/NotificationContext';

// Custom TabPanel component for displaying tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`commands-tabpanel-${index}`}
      aria-labelledby={`commands-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function CommandList() {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [commandsData, setCommandsData] = useState({
    custom_commands: [],
    global_commands: []
  });
  const [updatingCommandIds, setUpdatingCommandIds] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deletingCommandIds, setDeletingCommandIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const initialRenderComplete = useRef(false);

  // Fetch commands data - only on initial mount
  useEffect(() => {
    // Skip if not first render
    if (initialRenderComplete.current) return;
    
    const fetchCommands = async () => {
      setLoading(true);
      try {
        const response = await Requester.get('command/list/');
        if (response.ok) {
          const data = await response.json();
          setCommandsData(data);
        } else {
          console.error('Failed to fetch commands');
          showNotification(t('commands_list.command_load_error'), {
            type: 'error',
            autoHideDuration: 3000
          });
        }
      } catch (error) {
        console.error('Error fetching commands:', error);
        showNotification(t('commands_list.command_load_error'), {
          type: 'error',
          autoHideDuration: 3000
        });
      } finally {
        setLoading(false);
        // Mark initial render as complete after fetch
        initialRenderComplete.current = true;
      }
    };

    fetchCommands();
  }, []); // Empty dependency array means this runs only once on mount

  // Handle toggling command active status
  const handleToggleActive = async (command, isActive) => {
    // Prevent duplicate updates on same command
    if (updatingCommandIds.includes(command.id)) return;
    
    // Mark as updating and immediately update UI (optimistic update)
    setUpdatingCommandIds(prev => [...prev, command.id]);
    setCommandsData(prevData => ({
      custom_commands: prevData.custom_commands.map(cmd => 
        cmd.id === command.id ? { ...cmd, is_active: isActive } : cmd
      ),
      global_commands: prevData.global_commands.map(cmd => 
        cmd.id === command.id ? { ...cmd, is_active: isActive } : cmd
      )
    }));
    
    try {
      const payload = {
        id: command.id,
        is_active: isActive,
      };
      
      const response = await Requester.post('command/update/', payload);
      
      if (!response.ok) {
        // Revert the optimistic update if API call fails
        setCommandsData(prevData => ({
          custom_commands: prevData.custom_commands.map(cmd => 
            cmd.id === command.id ? { ...cmd, is_active: !isActive } : cmd
          ),
          global_commands: prevData.global_commands.map(cmd => 
            cmd.id === command.id ? { ...cmd, is_active: !isActive } : cmd
          )
        }));
        
        console.error('Failed to update command');
        showNotification(t('commands_list.command_update_error'), {
          type: 'error',
          autoHideDuration: 3000
        });
      } else {
        // Success notification (UI already updated)
        showNotification(isActive ? t('commands_list.command_activated') : t('commands_list.command_deactivated'), {
          type: 'success',
          autoHideDuration: 3000
        });
      }
    } catch (error) {
      // Revert on error
      setCommandsData(prevData => ({
        custom_commands: prevData.custom_commands.map(cmd => 
          cmd.id === command.id ? { ...cmd, is_active: !isActive } : cmd
        ),
        global_commands: prevData.global_commands.map(cmd => 
          cmd.id === command.id ? { ...cmd, is_active: !isActive } : cmd
        )
      }));
      
      console.error('Error updating command:', error);
      showNotification(t('commands_list.command_update_error'), {
        type: 'error',
        autoHideDuration: 3000
      });
    } finally {
      // Clear updating state
      setUpdatingCommandIds(prev => prev.filter(id => id !== command.id));
    }
  };

  // Handle deleting a command
  const handleDeleteCommand = async (command) => {
    if (window.confirm(t('commands_list.confirm_delete'))) {
      setDeletingCommandIds(prev => [...prev, command.id]);
      
      try {
        const response = await Requester.delete(`command/delete/${command.id}/`);
        
        if (response.ok) {
          // Update local state by removing the deleted command
          setCommandsData(prevData => {
            return {
              custom_commands: prevData.custom_commands.filter(cmd => cmd.id !== command.id),
              global_commands: prevData.global_commands.filter(cmd => cmd.id !== command.id)
            };
          });
          
          showNotification(t('commands_list.command_deleted'), {
            type: 'success',
            autoHideDuration: 3000
          });
        } else {
          console.error('Failed to delete command');
          showNotification(t('commands_list.delete_failed'), {
            type: 'error',
            autoHideDuration: 3000
          });
        }
      } catch (error) {
        console.error('Error deleting command:', error);
        showNotification(t('commands_list.delete_error'), {
          type: 'error',
          autoHideDuration: 3000
        });
      } finally {
        setDeletingCommandIds(prev => prev.filter(id => id !== command.id));
      }
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle closing snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle opening the actions menu
  const handleOpenActionsMenu = (event, command) => {
    setAnchorEl(event.currentTarget);
    setSelectedCommand(command);
  };

  // Handle closing the actions menu
  const handleCloseActionsMenu = () => {
    setAnchorEl(null);
    setSelectedCommand(null);
  };

  // Handle edit action from menu
  const handleEditFromMenu = () => {
    handleCloseActionsMenu();
    // The router will navigate to the edit page
  };

  // Handle delete action from menu
  const handleDeleteFromMenu = () => {
    if (selectedCommand) {
      handleDeleteCommand(selectedCommand);
    }
    handleCloseActionsMenu();
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              to="/dashboard"
              component={RouterLink}
              sx={{
                '&:hover': { color: 'primary.main' }
              }}
            >
              {t('dashboard_navigation.home')}
            </Link>
            <Typography color="primary">{t('dashboard_navigation.commands')}</Typography>
          </Breadcrumbs>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" fontWeight="500">
                {t('commands_list.commands_title')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              component={RouterLink}
              to="/dashboard/commands/create"
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {t('commands_list.create_new_command')}
            </Button>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="commands tabs">
                <Tab label={t('commands_list.custom_commands_tab')} />
                <Tab label={t('commands_list.global_commands_tab')} />
              </Tabs>
            </Box>

            {/* Custom Commands Tab */}
            <TabPanel value={tabValue} index={0}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : commandsData.custom_commands.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1">{t('commands_list.no_commands')}</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="custom commands table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('commands_list.enabled_column')}</TableCell>
                          <TableCell>{t('commands_list.command_prefix_column')}</TableCell>
                          <TableCell>{t('commands_list.command_type_column')}</TableCell>
                          <TableCell width="60px" align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commandsData.custom_commands
                          .map((command) => (
                            <TableRow key={command.id}>
                              <TableCell>
                                {updatingCommandIds.includes(command.id) ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  <Switch 
                                    checked={command.is_active} 
                                    onChange={(e) => handleToggleActive(command, e.target.checked)}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="body2" fontWeight="500">
                                    {command.command_prefix}
                                  </Typography>
                                  {(() => {
                                    // Try to get aliases from extra_data first, then fallback to direct aliases property
                                    let aliases = [];
                                    if (command.extra_data) {
                                      try {
                                        const extraData = typeof command.extra_data === 'string' 
                                          ? JSON.parse(command.extra_data) 
                                          : command.extra_data;
                                        aliases = extraData.aliases || [];
                                      } catch (e) {
                                        console.error('Error parsing extra_data:', e);
                                      }
                                    } else if (command.aliases) {
                                      aliases = command.aliases;
                                    }
                                    
                                    if (aliases.length > 0) {
                                      return (
                                        <Typography variant="body2" color="text.secondary">
                                          {aliases.slice(0, 2).join(' ')}
                                          {aliases.length > 2 && ` +${aliases.length - 2} more`}
                                        </Typography>
                                      );
                                    }
                                    return null;
                                  })()}
                                </Box>
                              </TableCell>
                              <TableCell>{command.command_class_description}</TableCell>
                              <TableCell width="60px" align="center">
                                <IconButton 
                                  aria-label="actions"
                                  onClick={(e) => handleOpenActionsMenu(e, command)}
                                  sx={{
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: 'primary.light',
                                    }
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </TabPanel>

            {/* Global Commands Tab */}
            <TabPanel value={tabValue} index={1}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : commandsData.global_commands.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1">{t('commands_list.no_commands')}</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="global commands table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('commands_list.enabled_column')}</TableCell>
                          <TableCell>{t('commands_list.command_prefix_column')}</TableCell>
                          <TableCell>{t('commands_list.command_type_column')}</TableCell>
                          <TableCell width="60px" align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commandsData.global_commands
                          .map((command) => (
                            <TableRow key={command.id}>
                              <TableCell>
                                {updatingCommandIds.includes(command.id) ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  <Switch 
                                    checked={command.is_active} 
                                    onChange={(e) => handleToggleActive(command, e.target.checked)}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="body2" fontWeight="500">
                                    {command.command_prefix}
                                  </Typography>
                                  {(() => {
                                    // Try to get aliases from extra_data first, then fallback to direct aliases property
                                    let aliases = [];
                                    if (command.extra_data) {
                                      try {
                                        const extraData = typeof command.extra_data === 'string' 
                                          ? JSON.parse(command.extra_data) 
                                          : command.extra_data;
                                        aliases = extraData.aliases || [];
                                      } catch (e) {
                                        console.error('Error parsing extra_data:', e);
                                      }
                                    } else if (command.aliases) {
                                      aliases = command.aliases;
                                    }
                                    
                                    if (aliases.length > 0) {
                                      return (
                                        <Typography variant="body2" color="text.secondary">
                                          {aliases.slice(0, 2).join(' ')}
                                          {aliases.length > 2 && ` +${aliases.length - 2} more`}
                                        </Typography>
                                      );
                                    }
                                    return null;
                                  })()}
                                </Box>
                              </TableCell>
                              <TableCell>{command.command_class_description}</TableCell>
                              <TableCell width="60px" align="center">
                                <IconButton 
                                  aria-label="actions"
                                  onClick={(e) => handleOpenActionsMenu(e, command)}
                                  sx={{
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: 'primary.light',
                                    }
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </TabPanel>
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseActionsMenu}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 150 }
          }}
        >
          <MenuItem 
            component={RouterLink}
            to={selectedCommand ? `/dashboard/commands/edit/${selectedCommand.id}` : '#'}
            onClick={handleEditFromMenu}
          >
            <ListItemText primary={t('commands_list.edit_command')} />
          </MenuItem>
          {/* Only show delete option for custom commands */}
          {tabValue === 0 && (
            <MenuItem 
              onClick={handleDeleteFromMenu}
              disabled={selectedCommand && deletingCommandIds.includes(selectedCommand.id)}
            >
              {selectedCommand && deletingCommandIds.includes(selectedCommand.id) ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              <ListItemText 
                primary={t('common.delete')} 
                sx={selectedCommand && deletingCommandIds.includes(selectedCommand.id) ? 
                  { color: 'text.disabled' } : { color: 'error.main' }
                }
              />
            </MenuItem>
          )}
        </Menu>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}